// LEGACY ADMIN APPROVAL UI
// This approval system has been deprecated.
// These views are kept for reference only and are not part of the current MVP.
//
// Influencer Application Detail View for Admin Dashboard
// Displays comprehensive influencer application details with approve/reject controls
// Used within the React routing system

import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const InfluencerDetail = ({ influencerId, onNavigate }) => {
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Fetch influencer data
  const fetchInfluencer = async () => {
    try {
      console.log('🔄 Fetching influencer details for:', influencerId);
      setLoading(true);
      setError(null);

      if (!db) {
        throw new Error('Firebase database not initialized');
      }

      const influencerDoc = await getDoc(doc(db, 'users', influencerId));
      
      if (!influencerDoc.exists()) {
        throw new Error('Influencer application not found');
      }

      const influencerData = influencerDoc.data();
      setInfluencer({ id: influencerDoc.id, ...influencerData });
      console.log('✅ Influencer data loaded:', influencerData.firstName || 'Unknown');
    } catch (err) {
      console.error('❌ Error fetching influencer:', err);
      setError(err instanceof Error ? err.message : 'Failed to load influencer details');
    } finally {
      setLoading(false);
    }
  };

  // Update influencer status
  const updateStatus = async (status) => {
    try {
      console.log(`🔄 Updating influencer ${influencerId} to ${status}...`);
      setUpdating(true);

      if (!db) {
        throw new Error('Firebase database not initialized');
      }

      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        ...(status === 'approved' ? { approvedAt: serverTimestamp() } : { rejectedAt: serverTimestamp() })
      };

      await updateDoc(doc(db, 'users', influencerId), updateData);
      
      // Update local state
      setInfluencer(prev => ({ ...prev, status, ...updateData }));
      
      // Show success message
      alert(`Influencer ${status} successfully!`);
      console.log(`✅ Influencer ${status} successfully`);
    } catch (err) {
      console.error('❌ Error updating influencer status:', err);
      alert(`Failed to ${status} influencer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case 'pending_approval':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'approved':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Approved</span>;
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>;
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    const name = `${firstName || ''} ${lastName || ''}`.trim();
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if (influencerId) {
      fetchInfluencer();
    }
  }, [influencerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Breadcrumb skeleton */}
          <div className="flex items-center space-x-2">
            <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
          </div>
          
          {/* Header card skeleton */}
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="flex-1 space-y-2">
                <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Content skeleton */}
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl border bg-white p-8 shadow-sm text-center">
            <div className="h-16 w-16 mx-auto mb-4 text-red-500 text-6xl">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Influencer</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={fetchInfluencer}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Try Again
              </button>
              <button
                onClick={() => onNavigate('admin')}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Back to Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!influencer) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl border bg-white p-8 shadow-sm text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Influencer Not Found</h1>
            <p className="text-gray-600 mb-6">The requested influencer application could not be found.</p>
            <button
              onClick={() => onNavigate('admin')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  const profile = influencer.profile || {};
  const account = profile.account || {};
  const audience = profile.audience || {};
  const bio = profile.bio || {};
  const socialLinks = profile.socialLinks || {};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500">
          <button
            onClick={() => onNavigate('admin')}
            className="hover:text-gray-700"
          >
            Admin
          </button>
          <span>›</span>
          <button
            onClick={() => onNavigate('admin')}
            className="hover:text-gray-700"
          >
            Influencers
          </button>
          <span>›</span>
          <span className="text-gray-900 font-medium">
            {influencer.firstName} {influencer.lastName}
          </span>
        </nav>

        {/* Header Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Avatar - Clickable to view full size */}
              {(() => {
                const photoUrl = influencer.photoUrl || profile.media?.profilePictureUrl || null;
                const displayName = `${influencer.firstName} ${influencer.lastName}`;
                
                return (
                  <div className="h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center overflow-hidden relative">
                    {photoUrl ? (
                      <a
                        href={photoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block w-full h-full"
                        title="Click to view full-size profile photo"
                      >
                        <img 
                          src={photoUrl} 
                          alt={displayName}
                          className="h-16 w-16 rounded-full object-cover border-2 border-slate-200 hover:border-blue-400 transition-colors cursor-pointer"
                          onError={(e) => {
                            if (e.target) {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement?.parentElement?.querySelector('.avatar-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }
                          }}
                        />
                      </a>
                    ) : null}
                    <div className={`avatar-fallback absolute inset-0 flex items-center justify-center ${photoUrl ? 'hidden' : ''}`}>
                      <span className="text-2xl font-bold text-purple-600">
                        {getInitials(influencer.firstName, influencer.lastName)}
                      </span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Influencer Info */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {influencer.firstName} {influencer.lastName}
                </h1>
                <div className="flex items-center space-x-4 mt-2">
                  {getStatusBadge(influencer.status)}
                  <span className="text-sm text-gray-500">
                    Submitted {formatDate(influencer.submittedAt || influencer.createdAt)}
                  </span>
                </div>
                {influencer.handle && (
                  <p className="text-sm text-gray-600 mt-1">@{influencer.handle}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {/* LEGACY: Manual approval controls have been deprecated in the current version. */}
              <p className="text-sm text-gray-500">
                Manual approval controls have been deprecated in the current version.
              </p>
            </div>
          </div>
        </div>

        {/* Application Details Card */}
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              {influencer.firstName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">First Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{influencer.firstName}</dd>
                </div>
              )}
              
              {influencer.lastName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{influencer.lastName}</dd>
                </div>
              )}
              
              {influencer.email && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a 
                      href={`mailto:${influencer.email}`}
                      className="text-blue-600 hover:text-blue-500"
                    >
                      {influencer.email}
                    </a>
                  </dd>
                </div>
              )}
              
              {account.phone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{account.phone}</dd>
                </div>
              )}
              
              {influencer.handle && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Handle</dt>
                  <dd className="mt-1 text-sm text-gray-900">@{influencer.handle}</dd>
                </div>
              )}
            </div>

            {/* Audience Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Audience Information</h3>
              
              {audience.totalFollowers && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Followers</dt>
                  <dd className="mt-1 text-sm text-gray-900">{audience.totalFollowers.toLocaleString()}</dd>
                </div>
              )}
              
              {audience.engagementRate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Engagement Rate</dt>
                  <dd className="mt-1 text-sm text-gray-900">{audience.engagementRate}%</dd>
                </div>
              )}
              
              {audience.niches?.length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-2">Niches</dt>
                  <dd className="flex flex-wrap gap-2">
                    {audience.niches.map((niche, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {niche}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {bio.text && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Bio</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{bio.text}</p>
            </div>
          )}

          {/* Social Links */}
          {Object.keys(socialLinks).length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Social Links</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(socialLinks).map(([platform, url]) => (
                  <div key={platform}>
                    <dt className="text-sm font-medium text-gray-500 capitalize">{platform}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a 
                        href={url.startsWith('http') ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-500"
                      >
                        {url}
                      </a>
                    </dd>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Screenshot */}
          {audience.analyticsScreenshotUrl && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Analytics Screenshot</h3>
              <div className="max-w-md">
                <img 
                  src={audience.analyticsScreenshotUrl} 
                  alt="Analytics Screenshot"
                  className="w-full h-auto rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                  onClick={() => window.open(audience.analyticsScreenshotUrl, '_blank')}
                />
                <p className="text-xs text-gray-500 mt-2">Click to view full size</p>
              </div>
            </div>
          )}

          {/* Shipping Information */}
          {account.contact?.shipping && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Shipping Address</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  {account.contact.shipping.address1}<br />
                  {account.contact.shipping.address2 && `${account.contact.shipping.address2}<br />`}
                  {account.contact.shipping.city}, {account.contact.shipping.state} {account.contact.shipping.zip}<br />
                  {account.contact.shipping.country}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer Actions (Mobile) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
          <div className="flex space-x-3">
            <button
              onClick={() => onNavigate('admin')}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            {/* LEGACY: Manual approval controls have been deprecated in the current version. */}
            <div className="flex-1 px-4 py-2 text-sm text-gray-500 text-center">
              Approval system deprecated
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerDetail;


